// api.ts
import axios, { Method } from "axios";
import Cookies from "js-cookie";

const urlsPath =
  process.env.REACT_APP_BACKEND_API_URL || "http://localhost:80/api/v1/webapp";

const unauthorizedExertionRoutes = [
  "/",
  "/resetPassword",
  "/ForgotPassword",
  "/ResetPasswordOTP",
];

// Instead of a global token variable, we always read from cookies.
let refreshToken: string | null = localStorage.getItem("refreshToken");
let patientIndex: string | null = null;

/**
 * Utility function to get the access token from cookies.
 */
function getAccessToken(): string | undefined {
  return Cookies.get("access_token");
}

/**
 * Logs the user out by removing tokens and redirecting to the login page.
 */
export function handleLogout() {
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  window.location.replace("/"); // Redirect to login page
}

/**
 * Create an Axios instance with base configuration.
 */
const apiInstance = axios.create({
  baseURL: urlsPath,
  timeout: 10000,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

/**
 * Request interceptor: Attach the latest access token to every request.
 */
apiInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor: Log errors and handle unauthorized responses.
 */
apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log("API Error:", error.response?.status, error.response?.data);
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn("Unauthorized! Logging out.");
      if (getAccessToken() && getAccessToken() !== "logout") {
        handleLogout();
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Sets refreshed authentication data.
 * Updates the access token in cookies and the refresh token in localStorage.
 */
function setRefreshedAuthData(
  authToken: string,
  rToken: string,
  patientI?: string
) {
  Cookies.set("access_token", authToken);
  setAPIRefreshToken(rToken);
  if (patientI) {
    patientIndex = patientI;
  }
}

/**
 * Update the refresh token.
 */
function setAPIRefreshToken(rToken: string) {
  refreshToken = rToken;
  localStorage.setItem("refreshToken", rToken);
}

/**
 * Optionally remove the in-memory authorization token.
 */
export function removeAPIAuthToken() {
  refreshToken = null;
}

/**
 * Optionally set the API timeout.
 */
export function setAPITimeout(timeout: number) {
  apiInstance.defaults.timeout = timeout;
}

/**
 * Generic API request function.
 */
export async function API_Root(url: string, method: Method, data: any) {
  try {
    return await apiInstance.request({
      url,
      method,
      data,
      validateStatus: (status) =>
        (status >= 200 && status < 300) ||
        (status >= 400 && status < 500) ||
        status === 504,
    });
  } catch (error: any) {
    const captureMessage = `API: ${url}, ${method}, ${JSON.stringify(
      data
    )}, ${JSON.stringify(error)} ${error.response?.data?.message ?? "-"}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}

/**
 * Check whether the access token exists (is valid).
 */
async function isAccessTokenValid(): Promise<boolean> {
  return !!getAccessToken();
}

/*=============================GENERIC FUNCTIONS======================================*/

/**
 * Generic HTTP POST request.
 */
async function makePostRequest(url: string, data: any) {
  try {
    const res = await apiInstance.request({
      url,
      method: "POST",
      data,
      withCredentials: true,
    });
    if (
      (url === "/users/Login" ||
        url === "/users/validate-reset-password-otp") &&
      res?.data?.data?.access_token
    ) {
      const newToken = res.data.data.access_token;
      Cookies.set("access_token", newToken);
      localStorage.setItem("access_token", newToken);
    }
    return res;
  } catch (error) {
    const captureMessage = `apiPostRequest (err): ${url}, ${JSON.stringify(
      error
    )}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}

/**
 * Generic HTTP PATCH request.
 */
async function makePatchRequest(url: string, data: any) {
  try {
    return await apiInstance.request({
      url,
      method: "PATCH",
      data,
      headers: { Authorization: `Bearer ${getAccessToken()}` },
    });
  } catch (error) {
    const captureMessage = `apiPatchRequest (err): ${url}, ${JSON.stringify(
      error
    )}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}
async function makePutRequest(url: string, data: any) {
  try {
    return await apiInstance.request({
      url,
      method: "PUT",
      data,
      withCredentials: true,
    });
  } catch (error) {
    const captureMessage = `apiPutRequest (err): ${url}, ${JSON.stringify(
      error
    )}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}

/**
 * Generic HTTP GET request.
 */
async function makeGetRequest(url: string, params: any = undefined) {
  try {
    return await apiInstance.request({
      url,
      method: "GET",
      params,
      headers: { Authorization: `Bearer ${getAccessToken()}` },
      withCredentials: true,
    });
  } catch (error) {
    const captureMessage = `apiGetRequest (err): ${url}, ${JSON.stringify(
      error
    )}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}

/**
 * Generic HTTP DELETE request.
 */
async function makeDeleteRequest(url: string, data: any = {}) {
  try {
    return await apiInstance.request({
      url,
      method: "DELETE",
      data,
    });
  } catch (error) {
    const captureMessage = `apiDeleteRequest (err): ${url}, ${JSON.stringify(
      error
    )}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}

/**
 * Generic upload request for multipart/form-data.
 */
async function makeUploadRequest(url: string, data: any) {
  try {
    return await apiInstance.request({
      url,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data,
      validateStatus: (status) =>
        (status >= 200 && status < 300) || (status >= 400 && status < 500),
    });
  } catch (error) {
    const captureMessage = `apiUploadRequest: ${url}\n ${JSON.stringify(
      error,
      null,
      2
    )}`;
    console.log(captureMessage, "debug");
    return Promise.reject(error);
  }
}

/**
 * Generic blob POST request.
 */
async function makeBlobPostRequest(url: string, data: any): Promise<Blob> {
  try {
    const res = await apiInstance.request({
      url,
      method: "POST",
      data,
      withCredentials: true,
      headers: { Authorization: `Bearer ${getAccessToken()}` },
      responseType: "blob",
    });
    return res.data as Blob;
  } catch (error) {
    console.error(
      `makeBlobPostRequest (err): ${url}, ${JSON.stringify(error)}`
    );
    throw error;
  }
}

async function makeBlobGetRequest(url: string, data: any): Promise<Blob> {
  try {
    const res = await apiInstance.request({
      url,
      method: "GET",
      data,
      withCredentials: true,
      headers: { Authorization: `Bearer ${getAccessToken()}` },
      responseType: "blob",
    });
    return res.data as Blob;
  } catch (error) {
    console.error(`makeBlobGetRequest (err): ${url}, ${JSON.stringify(error)}`);
    throw error;
  }
}

export {
  urlsPath,
  makeGetRequest,
  makePostRequest,
  setAPIRefreshToken,
  makeUploadRequest,
  makeDeleteRequest,
  refreshToken,
  patientIndex,
  makePatchRequest,
  makeBlobPostRequest,
  setRefreshedAuthData,
  isAccessTokenValid,
  makeBlobGetRequest,
  makePutRequest,
};
